import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const FinanceDashboard: React.FC = () => {
  const financeKPIs = [{
    title: 'Revenue',
    value: '₹9,250 Cr',
    change: '+5.2%',
    isPositive: true,
    subtitle: 'Total Revenue YTD'
  }, {
    title: 'EBITDA %',
    value: '18.3%',
    change: '+0.8%',
    isPositive: true,
    subtitle: 'EBITDA Margin'
  }, {
    title: 'PAT %',
    value: '11.2%',
    change: '+0.5%',
    isPositive: true,
    subtitle: 'Net Profit Margin'
  }, {
    title: 'Working Capital Days',
    value: '46',
    change: '-3 days',
    isPositive: true,
    subtitle: 'WC Cycle'
  }];
  // Revenue/EBITDA/PAT Trend Data
  const financialTrendData = [{
    quarter: 'Q1 FY24',
    revenue: 2180,
    ebitda: 385,
    pat: 238
  }, {
    quarter: 'Q2 FY24',
    revenue: 2245,
    ebitda: 398,
    pat: 248
  }, {
    quarter: 'Q3 FY24',
    revenue: 2310,
    ebitda: 415,
    pat: 260
  }, {
    quarter: 'Q4 FY24',
    revenue: 2515,
    ebitda: 496,
    pat: 290
  }];
  // Cash Flow Waterfall Data
  const cashFlowData = [{
    name: 'Opening Cash',
    value: 485,
    color: '#3b82f6'
  }, {
    name: 'Operating CF',
    value: 1245,
    color: '#10b981'
  }, {
    name: 'Capex',
    value: -385,
    color: '#ef4444'
  }, {
    name: 'Working Capital',
    value: -142,
    color: '#f59e0b'
  }, {
    name: 'Debt Repayment',
    value: -285,
    color: '#ef4444'
  }, {
    name: 'Dividends',
    value: -165,
    color: '#ef4444'
  }, {
    name: 'Closing Cash',
    value: 753,
    color: '#8b5cf6'
  }];
  // Cost Structure Distribution
  const costStructureData = [{
    name: 'Raw Materials',
    value: 4850,
    color: '#3b82f6'
  }, {
    name: 'Labor',
    value: 1285,
    color: '#10b981'
  }, {
    name: 'Overheads',
    value: 942,
    color: '#f59e0b'
  }, {
    name: 'Depreciation',
    value: 485,
    color: '#ef4444'
  }, {
    name: 'Interest',
    value: 245,
    color: '#8b5cf6'
  }, {
    name: 'Other',
    value: 307,
    color: '#ec4899'
  }];
  // Quarterly Financial Summary
  const quarterlyData = [{
    quarter: 'Q1 FY24',
    revenue: 2180,
    cogs: 1550,
    grossProfit: 630,
    grossMargin: 28.9,
    ebitda: 385,
    ebitdaMargin: 17.7,
    pat: 238,
    patMargin: 10.9,
    wcDays: 49
  }, {
    quarter: 'Q2 FY24',
    revenue: 2245,
    cogs: 1595,
    grossProfit: 650,
    grossMargin: 29.0,
    ebitda: 398,
    ebitdaMargin: 17.7,
    pat: 248,
    patMargin: 11.0,
    wcDays: 48
  }, {
    quarter: 'Q3 FY24',
    revenue: 2310,
    cogs: 1635,
    grossProfit: 675,
    grossMargin: 29.2,
    ebitda: 415,
    ebitdaMargin: 18.0,
    pat: 260,
    patMargin: 11.3,
    wcDays: 47
  }, {
    quarter: 'Q4 FY24',
    revenue: 2515,
    cogs: 1780,
    grossProfit: 735,
    grossMargin: 29.2,
    ebitda: 496,
    ebitdaMargin: 19.7,
    pat: 290,
    patMargin: 11.5,
    wcDays: 46
  }];
  return <div data-id="element-3987">
      <DescriptionPanel title="Financial Performance Overview" description="Comprehensive financial analysis covering revenue trends, profitability metrics, cash flow dynamics, and cost structure. Monitor key financial indicators and quarterly performance to drive strategic decision-making and operational excellence." insights={['Revenue grew 5.2% YoY to ₹9,250 Cr with strong Q4 performance', 'EBITDA margin expanded to 18.3%, up 80 bps from last year', 'Working capital cycle improved to 46 days from 49 days', 'Operating cash flow of ₹1,245 Cr supporting growth investments']} data-id="element-3988" />
      {/* Finance KPI Row */}
      <div className="mb-8" data-id="element-3989">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-3990">
          Key Financial Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-3991">
          {financeKPIs.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-3992">
              <div className="flex flex-col h-full" data-id="element-3993">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-3994">
                  {kpi.title}
                </div>
                <div className="text-xs text-gray-400 mb-3" data-id="element-3995">{kpi.subtitle}</div>
                <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-3996">
                  {kpi.value}
                </div>
                <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-3997">
                  {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-3998" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-3999" />}
                  {kpi.change} vs last year
                </div>
              </div>
            </Card>)}
        </div>
      </div>
      {/* Revenue/EBITDA/PAT Trend */}
      <Card className="p-6 mb-8" data-id="element-4000">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4001">
          Revenue, EBITDA & PAT Trend
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-4002">
          Quarterly financial performance (₹ Cr)
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-4003">
          <LineChart data={financialTrendData} data-id="element-4004">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4005" />
            <XAxis dataKey="quarter" tick={{
            fontSize: 12
          }} data-id="element-4006" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-4007" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: number) => `₹${value} Cr`} data-id="element-4008" />
            <Legend data-id="element-4009" />
            <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} name="Revenue" dot={{
            r: 5
          }} data-id="element-4010" />
            <Line type="monotone" dataKey="ebitda" stroke="#10b981" strokeWidth={3} name="EBITDA" dot={{
            r: 5
          }} data-id="element-4011" />
            <Line type="monotone" dataKey="pat" stroke="#8b5cf6" strokeWidth={3} name="PAT" dot={{
            r: 5
          }} data-id="element-4012" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4013">
          <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-4014">
            <div className="text-center" data-id="element-4015">
              <div className="text-gray-600" data-id="element-4016">Total Revenue FY24</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-4017">₹9,250 Cr</div>
            </div>
            <div className="text-center" data-id="element-4018">
              <div className="text-gray-600" data-id="element-4019">Total EBITDA FY24</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4020">₹1,694 Cr</div>
            </div>
            <div className="text-center" data-id="element-4021">
              <div className="text-gray-600" data-id="element-4022">Total PAT FY24</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-4023">₹1,036 Cr</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Cash Flow Waterfall and Cost Structure */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-4024">
        {/* Cash Flow Waterfall */}
        <Card className="p-6" data-id="element-4025">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4026">
            Cash Flow Waterfall - FY24
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4027">
            Opening to Closing Cash (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={300} data-id="element-4028">
            <BarChart data={cashFlowData} data-id="element-4029">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-4030" />
              <XAxis dataKey="name" tick={{
              fontSize: 10
            }} angle={-15} textAnchor="end" height={80} data-id="element-4031" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-4032" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${Math.abs(value)} Cr`} data-id="element-4033" />
              <Bar dataKey="value" data-id="element-4034">
                {cashFlowData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4035" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4036">
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-4037">
              <div className="text-center" data-id="element-4038">
                <div className="text-gray-600" data-id="element-4039">Net Cash Generated</div>
                <div className="text-lg font-bold text-green-600" data-id="element-4040">₹268 Cr</div>
              </div>
              <div className="text-center" data-id="element-4041">
                <div className="text-gray-600" data-id="element-4042">Cash Conversion %</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-4043">73.5%</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Cost Structure Distribution */}
        <Card className="p-6" data-id="element-4044">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-4045">
            Cost Structure Distribution
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-4046">Total Costs: ₹8,114 Cr</p>
          <ResponsiveContainer width="100%" height={300} data-id="element-4047">
            <PieChart data-id="element-4048">
              <Pie data={costStructureData} cx="50%" cy="50%" labelLine={false} label={({
              name,
              percent
            }) => `${name}: ${(percent * 100).toFixed(1)}%`} outerRadius={100} fill="#8884d8" dataKey="value" data-id="element-4049">
                {costStructureData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4050" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-4051" />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4052">
            <div className="grid grid-cols-3 gap-2" data-id="element-4053">
              {costStructureData.map((item, index) => <div key={index} className="flex items-center text-xs" data-id="element-4054">
                  <div className="w-3 h-3 rounded mr-2" style={{
                backgroundColor: item.color
              }} data-id="element-4055" />
                  <span className="text-gray-700" data-id="element-4056">{item.name}</span>
                </div>)}
            </div>
          </div>
        </Card>
      </div>
      {/* Quarterly Financial Summary Table */}
      <Card className="p-6" data-id="element-4057">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-4058">
          Quarterly Financial Summary - FY24
        </h3>
        <div className="overflow-x-auto" data-id="element-4059">
          <table className="w-full" data-id="element-4060">
            <thead data-id="element-4061">
              <tr className="border-b-2 border-gray-300" data-id="element-4062">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4063">
                  Quarter
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4064">
                  Revenue
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4065">
                  COGS
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4066">
                  Gross Profit
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4067">
                  Gross Margin %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4068">
                  EBITDA
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4069">
                  EBITDA %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4070">
                  PAT
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4071">
                  PAT %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-4072">
                  WC Days
                </th>
              </tr>
            </thead>
            <tbody data-id="element-4073">
              {quarterlyData.map((row, index) => <tr key={index} className={`border-b border-gray-100 hover:bg-gray-50 ${index === quarterlyData.length - 1 ? 'bg-blue-50 font-semibold' : ''}`} data-id="element-4074">
                  <td className="py-3 px-4 text-sm text-gray-800 font-medium" data-id="element-4075">
                    {row.quarter}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-800" data-id="element-4076">
                    ₹{row.revenue} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-4077">
                    ₹{row.cogs} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-blue-600 font-medium" data-id="element-4078">
                    ₹{row.grossProfit} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-blue-600 font-semibold" data-id="element-4079">
                    {row.grossMargin}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-green-600 font-medium" data-id="element-4080">
                    ₹{row.ebitda} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-green-600 font-semibold" data-id="element-4081">
                    {row.ebitdaMargin}%
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-purple-600 font-medium" data-id="element-4082">
                    ₹{row.pat} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-purple-600 font-semibold" data-id="element-4083">
                    {row.patMargin}%
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-4084">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.wcDays <= 46 ? 'text-green-600 bg-green-50' : row.wcDays <= 48 ? 'text-yellow-600 bg-yellow-50' : 'text-red-600 bg-red-50'}`} data-id="element-4085">
                      {row.wcDays} days
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-4086">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-4087">
            <div className="text-center" data-id="element-4088">
              <div className="text-gray-600" data-id="element-4089">FY24 Revenue</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-4090">₹9,250 Cr</div>
            </div>
            <div className="text-center" data-id="element-4091">
              <div className="text-gray-600" data-id="element-4092">FY24 Gross Margin</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-4093">29.1%</div>
            </div>
            <div className="text-center" data-id="element-4094">
              <div className="text-gray-600" data-id="element-4095">FY24 EBITDA</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4096">₹1,694 Cr</div>
            </div>
            <div className="text-center" data-id="element-4097">
              <div className="text-gray-600" data-id="element-4098">FY24 PAT</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-4099">₹1,036 Cr</div>
            </div>
            <div className="text-center" data-id="element-4100">
              <div className="text-gray-600" data-id="element-4101">Avg WC Days</div>
              <div className="text-xl font-bold text-green-600" data-id="element-4102">47.5</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default FinanceDashboard;